// 청크 스테일 자동 회복 (frontend-stability Task A-1)
// 배경(Sentry): 배포 후 옛 세션이 사라진 해시 청크를 lazy 로드 → "Failed to fetch
// dynamically imported module" → 앱 전체 ErrorBoundary 교체.
// 동작: 로드 실패 → 1회 재시도 → 실패 시 세션당 1회 자동 새로고침(무한 루프 가드)
// → 그래도 실패면 isChunkError 마커를 단 에러로 EB에 전달("새 버전 배포" 안내).
import { lazy } from "react";

const RELOAD_FLAG = "mpx_chunk_reloaded";
// /client(광고주 공유)는 저장소 차단 브라우저 가능 → 메모리 플래그 폴백 (Task A-2 연계)
let memReloaded = false;

function hasReloaded() {
  try {
    if (sessionStorage.getItem(RELOAD_FLAG) === "1") return true;
  } catch {
    /* 저장소 차단 — 메모리 플래그만 */
  }
  return memReloaded;
}

function markReloaded() {
  memReloaded = true;
  try {
    sessionStorage.setItem(RELOAD_FLAG, "1");
  } catch {
    /* 저장소 차단 — 메모리 플래그만 */
  }
}

function clearReloaded() {
  memReloaded = false;
  try {
    sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    /* noop */
  }
}

// 순수 재시도 코어 — node 회귀 테스트 직접 호출용 export.
// reloadFn 주입 가능(테스트), 기본 = window.location.reload.
export async function retryImport(factory, name = "", reloadFn = null) {
  try {
    const mod = await factory();
    clearReloaded(); // 정상 로드 → 다음 배포 때도 자동 회복 1회 가능
    return mod;
  } catch (e1) {
    void e1;
    try {
      const mod = await factory(); // 1회 재시도 (일시 네트워크/CDN)
      clearReloaded();
      return mod;
    } catch (e2) {
      if (!hasReloaded()) {
        markReloaded();
        (reloadFn || (() => window.location.reload()))();
        return new Promise(() => {}); // reload 진행 동안 렌더 보류
      }
      const err = new Error(
        `새 버전이 배포되었습니다 — 새로고침이 필요합니다${name ? ` (${name})` : ""}`,
      );
      err.cause = e2;
      err.isChunkError = true;
      throw err;
    }
  }
}

export function lazyWithRetry(factory, name = "") {
  return lazy(() => retryImport(factory, name));
}
